import { ColDef, CellClassParams, ColGroupDef, ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { formatDateKey, trimStringValue } from "src/utils/string/stringUtil";
import { defaultGridCellBorderClass } from "src/utils/grid/gridStyleUtils";
import {
	MonthDayColumn,
	buildDateKey,
	createMonthDayColumnMeta,
	extractDayDdFromDateKey,
	formatDayGroupColId,
	formatDayRoundColId,
	getDaysInMonth,
	mergeMonthDayHeaderClasses,
	parseDayRoundColId,
} from "src/utils/date/dateUtils";
import {
	defaultEQM2080_R01DetailDto,
	EQM2080_R01DetailDto,
	EQM2080_R01DetailPivotDto,
	EQM2080_R01InspectMasterDto,
	EQM2080_R01DayCellValue,
} from "./Dto_EQM2080_R01";

/** EQM2080 기본 왼쪽 틀 고정 대상 colId */
export const EQM2080_DEFAULT_PINNED_COL_IDS = [
	"rowIndex",
	"inspectNm",
	"inspectItemNm",
	"standardValue",
] as const;

/** 상세 그리드 컬럼 ID → 헤더명 매핑 */
export const headerNames_EQM2080_R01Detail: Record<string, string> = {
	rowIndex: "",
	eqmType: "설비유형",
	inspectCd: "점검코드",
	inspectNm: "공정명",
	inspectItemNm: "점검항목",
	standardValue: "기준치",
};

/** 일자 셀에 저장할 값이 있는지 여부 */
export function hasDayCellValue(value: unknown): boolean {
	return trimStringValue(value) !== "";
}

/** pivot 일자 셀 변경 (1차→dayValues, 2차→dayValues2) */
export function applyPivotDayCellUpdate(
	updatedData: EQM2080_R01DetailPivotDto,
	field: string,
	newValue: unknown,
	originalRow?: EQM2080_R01DetailPivotDto,
): EQM2080_R01DetailPivotDto | null {
	const parsed = parseDayRoundColId(field);
	if (!parsed) return null;

	const { dayDd, round } = parsed;
	const normalizedValue = trimStringValue(newValue);
	const valueKey = round === 1 ? "dayValues" : "dayValues2";
	const originalDayValue = originalRow?.[valueKey]?.[dayDd] ?? "";
	const isModified = updatedData.inserted
		? !!updatedData.isModified
		: String(originalDayValue) !== String(normalizedValue);

	if (round === 1) {
		return {
			...updatedData,
			dayValues: {
				...updatedData.dayValues,
				[dayDd]: normalizedValue,
			},
			isModified,
		};
	}

	return {
		...updatedData,
		dayValues2: {
			...updatedData.dayValues2,
			[dayDd]: normalizedValue,
		},
		isModified,
	};
}

/** 2단 헤더(N일 → 1차/2차) 일자 컬럼 ColDef 생성 */
export function buildMonthDayGroupColumnDefs(
	inspectDtYm: string,
): ColDef<EQM2080_R01DetailPivotDto>[] {
	const createDayRoundSetter =
		(dayDd: string, round: 1 | 2) =>
		(params: ValueSetterParams<EQM2080_R01DetailPivotDto, string>) => {
			if (!params.data) return false;
			const valueKey = round === 1 ? "dayValues" : "dayValues2";
			const nextValue = trimStringValue(params.newValue);
			const currentMap = params.data[valueKey];
			if ((currentMap?.[dayDd] ?? "") === nextValue) return false;
			if (!params.data[valueKey]) {
				params.data[valueKey] = {};
			}
			// paddedGridData는 행 객체를 shallow copy하므로 dayValues 객체 참조를 유지한 채 in-place 갱신해야 store와 동기화된다.
			params.data[valueKey][dayDd] = nextValue;
			return true;
		};

	return getMonthDayColumns_EQM2080_R01(inspectDtYm).map((column) => {
		if (!column.dayDd || !column.colId1 || !column.colId2 || !column.groupColId) {
			return null;
		}
		return {
			headerName: column.headerName,
			colId: column.groupColId,
			headerClass: mergeMonthDayHeaderClasses("common-group-header", column.headerClass),
			children: [
				{
					headerName: "1차",
					colId: column.colId1,
					field: column.colId1,
					width: 44,
					editable: true,
					cellStyle: { textAlign: "center" },
					valueGetter: (params: ValueGetterParams<EQM2080_R01DetailPivotDto>) =>
						params.data?.dayValues?.[column.dayDd!] ?? "",
					valueSetter: createDayRoundSetter(column.dayDd, 1),
				},
				{
					headerName: "2차",
					colId: column.colId2,
					field: column.colId2,
					width: 44,
					editable: true,
					cellStyle: { textAlign: "center" },
					valueGetter: (params: ValueGetterParams<EQM2080_R01DetailPivotDto>) =>
						params.data?.dayValues2?.[column.dayDd!] ?? "",
					valueSetter: createDayRoundSetter(column.dayDd, 2),
				},
			],
		};
	}).filter(Boolean) as ColDef<EQM2080_R01DetailPivotDto>[];
}

/** 연속된 동일 inspectNm(공정명) 행의 rowSpan Map (valueIndex → span, 0 = 숨김) */
export function buildInspectNmMap(rows: EQM2080_R01DetailPivotDto[]): Map<number, number> {
	const spanMap = new Map<number, number>();
	const dataRows = rows.filter((row) => row.inspectCd);

	let index = 0;
	while (index < dataRows.length) {
		const currentNm = dataRows[index].inspectNm ?? "";
		let span = 1;

		while (
			index + span < dataRows.length &&
			(dataRows[index + span].inspectNm ?? "") === currentNm
		) {
			span += 1;
		}

		spanMap.set(dataRows[index].valueIndex, span);
		for (let offset = 1; offset < span; offset += 1) {
			spanMap.set(dataRows[index + offset].valueIndex, 0);
		}

		index += span;
	}

	return spanMap;
}

/** valueIndex 기준 inspectNm rowSpan 조회 (0 = 숨김, 없으면 1) */
export function getInspectNmRowSpan(
	spanMap: Map<number, number>,
	valueIndex?: number,
): number {
	if (!valueIndex) return 1;
	return spanMap.get(valueIndex) ?? 1;
}

/** 공정명 rowSpan 병합 셀 class */
export function getInspectNmCellClass(
	params: CellClassParams<EQM2080_R01DetailPivotDto>,
	spanMap: Map<number, number>,
): string {
	if (params.node?.rowPinned || !params.data?.inspectCd) {
		return defaultGridCellBorderClass;
	}

	const span = getInspectNmRowSpan(spanMap, params.data.valueIndex);
	if (span === 0) {
		return `eqm2080-inspect-nm-hidden ${defaultGridCellBorderClass}`;
	}
	if (span > 1) {
		return `eqm2080-inspect-nm-merged ${defaultGridCellBorderClass}`;
	}
	return defaultGridCellBorderClass;
}

/** 점검코드+일자로 flat형 상세 Dto 비교 키 생성 */
function buildDetailKey(inspectCd: string, inspectDt: Date | string | undefined): string {
	return `${inspectCd}_${formatDateKey(inspectDt)}`;
}

/** 점검년월 기준 1일~말일 동적 컬럼 목록 생성 */
export function getMonthDayColumns_EQM2080_R01(inspectDtYm: string): MonthDayColumn[] {
	const [year, month] = inspectDtYm.split("-");
	if (!year || !month) return [];

	const days = getDaysInMonth(inspectDtYm);
	return Array.from({ length: days }, (_, index) => {
		const day = index + 1;
		const dayDd = String(day).padStart(2, "0");
		const dayMeta = createMonthDayColumnMeta(day, inspectDtYm);
		return {
			...dayMeta,
			dayDd,
			colId1: formatDayRoundColId(1, day),
			colId2: formatDayRoundColId(2, day),
			groupColId: formatDayGroupColId(day),
		};
	});
}

/** pivot 행의 빈 일자별 점검값 맵 생성 (키: 2자리 일) */
function createEmptyDayValues_EQM2080_R01(inspectDtYm: string): Record<string, EQM2080_R01DayCellValue> {
	return Object.fromEntries(
		getMonthDayColumns_EQM2080_R01(inspectDtYm).map((column) => [column.dayDd!, ""]),
	) as Record<string, EQM2080_R01DayCellValue>;
}

/** flat형 상세 Dto DTO에서 inspect master 필드 추출 */
function toInspectMasterFromFlat(item: EQM2080_R01DetailDto): EQM2080_R01InspectMasterDto {
	return {
		eqmType: item.eqmType || "",
		eqmTypeNm: item.eqmTypeNm || "",
		inspectCd: item.inspectCd,
		inspectMethod: item.inspectMethod || "",
		inspectNm: item.inspectNm,
		remark: item.remark || "",
		standardValue: item.standardValue || "",
	};
}

/** inspectCd별 flat detail에서 공정명(remark) 추출 */
function buildRemarkByInspectCd(flatList: EQM2080_R01DetailDto[]): Map<string, string> {
	const remarkByInspectCd = new Map<string, string>();
	flatList.forEach((item) => {
		if (!item.remark || remarkByInspectCd.has(item.inspectCd)) return;
		remarkByInspectCd.set(item.inspectCd, item.remark);
	});
	return remarkByInspectCd;
}

/** flat형 상세 Dto + 점검기준을 pivot 그리드 행으로 변환 */
export function pivotDetailToGridRows(
	flatList: EQM2080_R01DetailDto[],
	inspectMasterList: EQM2080_R01InspectMasterDto[],
	inspectDtYm: string,
): EQM2080_R01DetailPivotDto[] {
	const rowSource: EQM2080_R01InspectMasterDto[] =
		inspectMasterList.length > 0
			? inspectMasterList
			: Array.from(
					new Map(
						flatList.map((item) => [item.inspectCd, toInspectMasterFromFlat(item)]),
					).values(),
				);

	const remarkByInspectCd = buildRemarkByInspectCd(flatList);

	return rowSource.map((inspect, index) => {
		const dayValues = createEmptyDayValues_EQM2080_R01(inspectDtYm);
		const dayValues2 = createEmptyDayValues_EQM2080_R01(inspectDtYm);
		const dayMeta: Record<string, { itemNo?: string }> = {};

		flatList
			.filter((item) => item.inspectCd === inspect.inspectCd)
			.forEach((item) => {
				const dateKey = formatDateKey(item.inspectDt);
				if (!dateKey.startsWith(inspectDtYm)) return;

				const dayDd = extractDayDdFromDateKey(dateKey);
				dayValues[dayDd] = trimStringValue(item.inspectValue);
				dayValues2[dayDd] = trimStringValue(item.inspectValue2);
				if (item.itemNo) {
					dayMeta[dayDd] = { itemNo: item.itemNo };
				}
			});

		const detailInspectMethod = flatList.find(
			(item) => item.inspectCd === inspect.inspectCd,
		)?.inspectMethod;

		return {
			valueIndex: index + 1,
			eqmType: inspect.eqmType,
			eqmTypeNm: inspect.eqmTypeNm || "",
			inspectCd: inspect.inspectCd,
			inspectNm: remarkByInspectCd.get(inspect.inspectCd) || inspect.remark || "",
			inspectItemNm: inspect.inspectNm,
			inspectMethod: inspect.inspectMethod || detailInspectMethod || "",
			standardValue: inspect.standardValue || "",
			dayValues,
			dayValues2,
			dayMeta,
			isModified: false,
		};
	});
}

/** pivot 그리드 행을 저장용 flat형 상세 Dto 배열로 변환 */
export function flattenGridRowsToDetail(
	pivotList: EQM2080_R01DetailPivotDto[],
	base: Pick<
		EQM2080_R01DetailDto,
		"inspectNo" | "compCd" | "equipmentCd" | "inspectCycle" | "judgeEmpno" | "judgeEmpNm"
	>,
	inspectDtYm: string,
): EQM2080_R01DetailDto[] {
	const result: EQM2080_R01DetailDto[] = [];
	const monthDayColumns = getMonthDayColumns_EQM2080_R01(inspectDtYm);

	pivotList.forEach((row) => {
		monthDayColumns.forEach((column) => {
			const dayDd = column.dayDd;
			if (!dayDd) return;

			const inspectValue = row.dayValues[dayDd] ?? "";
			const inspectValue2 = row.dayValues2[dayDd] ?? "";
			if (!hasDayCellValue(inspectValue) && !hasDayCellValue(inspectValue2)) return;

			const dateKey = buildDateKey(inspectDtYm, dayDd);
			result.push({
				...defaultEQM2080_R01DetailDto,
				inspectNo: base.inspectNo,
				compCd: base.compCd,
				equipmentCd: base.equipmentCd,
				inspectCycle: base.inspectCycle,
				judgeEmpno: base.judgeEmpno,
				judgeEmpNm: base.judgeEmpNm,
				inspectCd: row.inspectCd,
				inspectMethod: row.inspectMethod,
				standardValue: row.standardValue,
				remark: row.inspectNm,
				inspectNm: row.inspectItemNm,
				inspectDt: dateKey,
				inspectValue: trimStringValue(inspectValue),
				inspectValue2: trimStringValue(inspectValue2),
				itemNo: row.dayMeta[dayDd]?.itemNo || "",
				inserted: !row.dayMeta[dayDd]?.itemNo,
				isModified: false,
			});
		});
	});

	return result;
}

/** 원본/현재 flat형 상세 Dto를 비교해 추가·수정·삭제 대상 추출 */
export function diffFlatDetailLists(
	original: EQM2080_R01DetailDto[],
	current: EQM2080_R01DetailDto[],
	inspectDtYm: string,
): EQM2080_R01DetailDto[] {
	const filterByMonth = (list: EQM2080_R01DetailDto[]) =>
		list.filter((item) => formatDateKey(item.inspectDt).startsWith(inspectDtYm));

	const originalFiltered = filterByMonth(original);
	const currentFiltered = filterByMonth(current);

	const originalMap = new Map(
		originalFiltered.map((item) => [buildDetailKey(item.inspectCd, item.inspectDt), item]),
	);
	const currentMap = new Map(
		currentFiltered.map((item) => [buildDetailKey(item.inspectCd, item.inspectDt), item]),
	);

	const inserted = currentFiltered.filter(
		(item) => !originalMap.has(buildDetailKey(item.inspectCd, item.inspectDt)),
	);
	const modified = currentFiltered
		.filter((item) => {
			const originalItem = originalMap.get(buildDetailKey(item.inspectCd, item.inspectDt));
			if (!originalItem) return false;
			return (
				trimStringValue(originalItem.inspectValue) !==
					trimStringValue(item.inspectValue) ||
				trimStringValue(originalItem.inspectValue2) !==
					trimStringValue(item.inspectValue2)
			);
		})
		.map((item) => ({
			...item,
			itemNo: originalMap.get(buildDetailKey(item.inspectCd, item.inspectDt))?.itemNo || item.itemNo,
			isModified: true,
			inserted: false,
		}));
	const deleted = originalFiltered
		.filter((item) => !currentMap.has(buildDetailKey(item.inspectCd, item.inspectDt)))
		.map((item) => ({
			...item,
			inspectValue: "",
			inspectValue2: "",
			isModified: true,
			inserted: false,
		}));

	return [...inserted, ...modified, ...deleted];
}
